import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { clsx } from 'clsx';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const StatCard = ({ title, value, subtext, type, icon }) => {
  const colors = {
    success: 'text-green-700 bg-green-50 border-green-200',
    warning: 'text-amber-700 bg-amber-50 border-amber-200',
    danger: 'text-red-700 bg-red-50 border-red-200',
    neutral: 'text-primary bg-primary/5 border-primary/20',
  };

  return (
    <Card className="relative overflow-hidden">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-muted-foreground mb-1">{title}</p>
          <h3 className="text-3xl font-bold text-foreground tracking-tight mb-1">{value}</h3>
          {subtext && <p className="text-xs text-muted-foreground">{subtext}</p>}
        </div>
        <div className={`p-3 rounded-lg border ${colors[type]}`}>
          <SafeIcon icon={icon} className="text-xl" />
        </div>
      </div>
    </Card>
  );
};

const PRIORITY_ORDER = { critical: 0, high: 1, medium: 2, standard: 3 };

const STATUS_DOT_COLORS = {
  'Pending': 'bg-slate-400',
  'In Progress': 'bg-blue-500',
  'Completed': 'bg-green-500',
  'Overdue': 'bg-red-500',
  'Stalled': 'bg-amber-500',
};

export const Dashboard = () => {
  const { data } = useApp();
  const { stats, ministries, alerts, ndp6 } = data;
  const navigate = useNavigate();

  // Cabinet Decision Tracker data (may not exist yet)
  const cabinetStats = data.cabinetStats;
  const cabinetDecisions = data.cabinetDecisions;

  const urgentDecisions = cabinetDecisions
    ?.filter(d => d.status !== 'Completed')
    .sort((a, b) => {
      const priorityDiff = (PRIORITY_ORDER[a.priority] ?? 99) - (PRIORITY_ORDER[b.priority] ?? 99);
      if (priorityDiff !== 0) return priorityDiff;
      return new Date(a.deadline) - new Date(b.deadline);
    })
    .slice(0, 3) ?? [];

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-bold text-foreground tracking-tight">National Strategic Command</h1>
          <p className="text-muted-foreground mt-1">Real-time oversight of government performance and accountability.</p>
        </div>
        <div className="bg-card px-4 py-2 rounded-lg border border-border shadow-sm text-sm">
          <span className="text-muted-foreground">Fiscal Year: </span>
          <span className="font-bold text-foreground">2025/2026</span>
        </div>
      </div>

      {/* KPI Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Projects On Track"
          value={stats.projectsOnTrack}
          subtext="68% of total portfolio"
          type="success"
          icon={FiIcons.FiCheckCircle}
        />
        <StatCard
          title="Projects At Risk"
          value={stats.projectsAtRisk}
          subtext="Requires intervention"
          type="warning"
          icon={FiIcons.FiAlertCircle}
        />
        <StatCard
          title="Critical Delays"
          value={stats.projectsDelayed}
          subtext="Immediate action needed"
          type="danger"
          icon={FiIcons.FiClock}
        />
        <StatCard
          title="Deadline Breaches"
          value={stats.deadlineBreaches}
          subtext="Legal escalations active"
          type="danger"
          icon={FiIcons.FiShield}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Column: Ministry Performance */}
        <div className="lg:col-span-2 space-y-6">
          <Card title="Ministry Performance Ranking (Top 5)">
            <div className="mb-6 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ministries} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <XAxis type="number" domain={[0, 100]} hide />
                  <YAxis 
                    type="category" 
                    dataKey="id" 
                    width={60} 
                    tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} 
                  />
                  <Tooltip 
                    cursor={{ fill: 'hsl(var(--muted))' }}
                    contentStyle={{ 
                      borderRadius: '8px', 
                      border: 'none', 
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                      backgroundColor: 'hsl(var(--card))'
                    }}
                  />
                  <Bar dataKey="score" radius={[0, 4, 4, 0]} barSize={24}>
                    {ministries.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.score > 70 ? '#009543' : entry.score > 50 ? '#C4953B' : '#D21034'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-muted text-muted-foreground uppercase text-xs border-b border-border">
                  <tr>
                    <th className="px-4 py-3 font-medium">Ministry</th>
                    <th className="px-4 py-3 font-medium">Performance Score</th>
                    <th className="px-4 py-3 font-medium">Budget (NAD)</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {ministries.map(min => (
                    <tr key={min.id} className="hover:bg-muted/50 transition-colors">
                      <td className="px-4 py-3 font-medium text-foreground">{min.name}</td>
                      <td className="px-4 py-3 font-bold">{min.score}%</td>
                      <td className="px-4 py-3 text-muted-foreground">{(min.budget / 1e9).toFixed(1)}B</td>
                      <td className="px-4 py-3">
                        <Badge status={min.status} size="sm" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* Right Column: Alerts & NDP6 */}
        <div className="space-y-6">
          {/* Critical Alerts */}
          <Card title="Critical Alerts Queue" className="border-l-4 border-l-destructive">
            <div className="space-y-4">
              {alerts.map(alert => (
                <div
                  key={alert.id}
                  className="p-3 bg-destructive/5 rounded-lg border border-destructive/20 group hover:border-destructive/40 transition-all cursor-pointer"
                >
                  <div className="flex justify-between items-start mb-1">
                    <span className="text-xs font-bold text-destructive uppercase">{alert.type}</span>
                    <span className="text-xs font-mono text-destructive">{alert.daysOverdue}d overdue</span>
                  </div>
                  <h4 className="text-sm font-bold text-foreground mb-1 leading-tight">{alert.title}</h4>
                  <p className="text-xs text-muted-foreground mb-2">{alert.message}</p>
                  <div className="flex justify-between items-center">
                    <Badge status="At Risk" size="sm">{alert.entity}</Badge>
                    <span className="text-[10px] text-muted-foreground">{alert.legalBasis}</span>
                  </div>
                </div>
              ))}
            </div>
            <Button variant="ghost" className="w-full mt-4">
              View Escalation Log
            </Button>
          </Card>

          {/* Cabinet Decision Tracker */}
          <Card title="Cabinet Decision Tracker" className="border-l-4 border-l-grn-gold">
            {/* Mini stats row */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-foreground">
                  {cabinetStats?.activeDecisions ?? '--'}
                </p>
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Active</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-foreground">
                  {cabinetStats?.avgImplementationRate != null ? `${cabinetStats.avgImplementationRate}%` : '--'}
                </p>
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Impl Rate</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <p className={clsx(
                  'text-2xl font-bold',
                  (cabinetStats?.overdueDecisions ?? 0) > 0 ? 'text-red-600' : 'text-foreground'
                )}>
                  {cabinetStats?.overdueDecisions ?? '--'}
                </p>
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Overdue</p>
              </div>
            </div>

            {/* Urgent decisions list */}
            {urgentDecisions.length > 0 ? (
              <div className="space-y-3">
                {urgentDecisions.map(decision => (
                  <div
                    key={decision.id}
                    className="p-3 bg-muted/30 rounded-lg border border-border hover:border-grn-gold/40 transition-all cursor-pointer"
                    onClick={() => navigate(`/cabinet-decisions/${decision.id}`)}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className={clsx(
                        'inline-block h-2.5 w-2.5 rounded-full flex-shrink-0',
                        STATUS_DOT_COLORS[decision.status] || 'bg-slate-400'
                      )} />
                      <span className="text-xs font-mono font-medium text-muted-foreground">
                        {decision.referenceNumber}
                      </span>
                    </div>
                    <h4 className="text-sm font-semibold text-foreground truncate mb-1">
                      {decision.title}
                    </h4>
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                      <span>{decision.leadMinistryId}</span>
                      <span className={clsx(
                        'font-medium',
                        decision.status === 'Overdue' && 'text-red-600 uppercase'
                      )}>
                        {decision.status === 'Overdue'
                          ? 'OVERDUE'
                          : `Due: ${new Date(decision.deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
                      </span>
                    </div>
                    {/* Progress bar */}
                    <div className="w-full bg-muted rounded-full h-1.5">
                      <div
                        className={clsx(
                          'h-1.5 rounded-full transition-all',
                          decision.status === 'Overdue' ? 'bg-red-500'
                            : (decision.overallProgress ?? 0) >= 75 ? 'bg-green-500'
                            : 'bg-blue-500'
                        )}
                        style={{ width: `${Math.min(decision.overallProgress ?? 0, 100)}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {decision.overallProgress ?? 0}% complete
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No active cabinet decisions to display.
              </p>
            )}

            <Button
              variant="ghost"
              className="w-full mt-4"
              onClick={() => navigate('/cabinet-decisions')}
            >
              <span className="flex items-center gap-2">
                View All Cabinet Decisions
                <SafeIcon icon={FiIcons.FiArrowRight} className="text-sm" />
              </span>
            </Button>
          </Card>

          {/* NDP6 Snapshot */}
          <Card title="NDP6 Progress Snapshot">
            <div className="space-y-4">
              {ndp6.pillars.map((pillar, i) => (
                <div key={i}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-medium text-foreground">{pillar.name}</span>
                    <span className={`text-xs font-bold ${pillar.status === 'Behind' ? 'text-destructive' : 'text-green-600'}`}>
                      {pillar.progress}%
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        pillar.status === 'Behind'
                          ? 'bg-destructive'
                          : pillar.status === 'At Risk'
                          ? 'bg-amber-500'
                          : 'bg-green-500'
                      }`}
                      style={{ width: `${pillar.progress}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};